import { tryCopyToClipboard } from "@core-ui/utils/clipboard";
import { cn } from "@core-ui/utils/cn";
import { AttachmentRow } from "@ext/agent/components/panel/AttachmentRow";
import { isImageAttachment } from "@ext/agent/components/utils/attachment";
import type { ChatAttachment } from "@ext/agent/core/attachmentStore";
import t from "@ext/localization/locale/translate";
import { IconButton } from "@ui-kit/Button";
import { Icon } from "@ui-kit/Icon";
import { OverflowTooltip, Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { type Ref, useEffect, useState } from "react";
import { SentFileAttachment } from "./SentFileAttachment";
import { SentImageAttachment } from "./SentImageAttachment";

export interface UserMessageProps {
	ts?: number;
	userText: string;
	attachments?: ChatAttachment[];
	quotedText?: string;
	stickyUserPrompt?: boolean;
	userPromptAnchorRef?: Ref<HTMLDivElement>;
}

export const UserMessage = ({
	ts,
	userText,
	attachments,
	quotedText,
	stickyUserPrompt = false,
	userPromptAnchorRef,
}: UserMessageProps) => {
	const [isCopied, setIsCopied] = useState(false);

	useEffect(() => {
		if (!isCopied) return undefined;

		const timeoutId = setTimeout(() => {
			setIsCopied(false);
		}, 1200);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [isCopied]);

	const handleCopy = () => {
		void tryCopyToClipboard(userText, { showPopover: false }).then((copied) => {
			if (copied) setIsCopied(true);
		});
	};

	return (
		<div
			className={cn(
				"group w-full min-w-0 pt-3 pb-2",
				stickyUserPrompt && cn("top-0 transition-[padding] duration-200 ease-out"),
			)}
			data-message-ts={ts}
			ref={userPromptAnchorRef}
		>
			{quotedText && (
				<div className="flex justify-end">
					<div className="flex max-w-[85%] min-w-0 items-center gap-1 pb-1 pr-1">
						<Icon className="size-3.5 shrink-0 text-muted-foreground" icon="corner-down-right" />
						<OverflowTooltip className="truncate min-w-0 text-sm text-muted-foreground">
							{quotedText}
						</OverflowTooltip>
					</div>
				</div>
			)}
			<div className="flex items-start justify-end gap-1">
				<div className="flex shrink-0 items-end justify-start pb-1 pt-1">
					<Tooltip>
						<TooltipTrigger asChild>
							<IconButton
								className={cn(
									"opacity-0 transition-all duration-150",
									"group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
									isCopied && "opacity-100",
								)}
								icon={isCopied ? "Check" : "Copy"}
								onClick={handleCopy}
								size="xs"
								variant="ghost"
							/>
						</TooltipTrigger>
						<TooltipContent>{t("copy")}</TooltipContent>
					</Tooltip>
				</div>
				<div className="flex max-w-[85%] min-w-0 flex-col items-stretch gap-1 rounded-xl rounded-br-[2px] border-secondary-border border-[0.5px] bg-background py-2 pl-3 pr-2.5">
					{attachments && attachments.length > 0 && (
						<AttachmentRow className="pt-1">
							{attachments.map((attachment) =>
								isImageAttachment(attachment) ? (
									<SentImageAttachment attachment={attachment} key={attachment.storagePath} />
								) : (
									<SentFileAttachment attachment={attachment} key={attachment.storagePath} />
								),
							)}
						</AttachmentRow>
					)}
					<div className="min-w-0 flex-1 text-sm text-primary-fg" data-agent-quotable>
						<span className="whitespace-pre-line break-words">{userText}</span>
					</div>
				</div>
			</div>
		</div>
	);
};
